import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ContestLeaderboardMapPanel from '@components/contest/ContestLeaderboardMapPanel';
import { ContestFrameShell } from '@components/contest/PublicContestFrames';
import { brandColors } from '@config/brandTokens';
import api from '@services/api';
import { getCountryFlagUrl, resolveCountryMeta, CONTEST_COUNTRY_META } from '@utils/contestGeos';

type LeaderboardLevel = 'city' | 'zone' | 'state' | 'national';
type LeaderboardView = 'standings' | 'map';

interface SkillOption {
  id: string;
  title: string;
  slug?: string;
}

interface CountryOption {
  name: string;
  code?: string | null;
}

interface LeaderboardEntry {
  rank: number;
  playerName: string;
  organizationName?: string | null;
  ageCategory?: string;
  city?: string | null;
  zone?: string | null;
  state?: string | null;
  country?: string | null;
  countryCode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  skillsCounted: number;
  totalScore: number;
  avgScore?: number;
  avgRating?: number | null;
  skillTitle?: string;
  skillScores?: { skillTitle?: string; score?: number; rating?: number | null }[];
  scope?: string;
}

interface ContestLeaderboardTabPanelProps {
  sportName: string;
  ageCategory: string;
  /** contest = approved video LB; self_eval = community self-assessment standings */
  mode?: 'contest' | 'self_eval';
}

const LEVELS: { id: LeaderboardLevel; label: string }[] = [
  { id: 'city', label: 'City' },
  { id: 'zone', label: 'Zone' },
  { id: 'state', label: 'State' },
  { id: 'national', label: 'National' },
];

/** Always-available country catalog for map / national filters (not limited to sports with scores). */
const PLATFORM_COUNTRY_OPTIONS: CountryOption[] = CONTEST_COUNTRY_META.map((c) => ({
  name: c.name,
  code: c.code,
})).sort((a, b) => a.name.localeCompare(b.name));

function mergeCountryOptions(fromApi: CountryOption[]): CountryOption[] {
  const byName = new Map<string, CountryOption>();
  for (const c of PLATFORM_COUNTRY_OPTIONS) {
    byName.set(c.name.toLowerCase(), { name: c.name, code: c.code || null });
  }
  for (const c of fromApi) {
    const name = String(c?.name || '').trim();
    if (!name) continue;
    const key = name.toLowerCase();
    const prev = byName.get(key);
    byName.set(key, {
      name: prev?.name || name,
      code: c.code || prev?.code || null,
    });
  }
  return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
}

const CountryFlagLabel: React.FC<{
  country?: string | null;
  countryCode?: string | null;
  size?: 'sm' | 'md';
}> = ({ country, countryCode, size = 'sm' }) => {
  const meta = resolveCountryMeta(country, countryCode);
  if (!meta) return <Typography variant="caption">—</Typography>;
  const flagUrl = getCountryFlagUrl(meta.code, size === 'md' ? 40 : 20);
  const imgH = size === 'md' ? 14 : 12;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.6,
        minWidth: 0,
      }}
      title={meta.name}
    >
      {flagUrl ? (
        <Box
          component="img"
          src={flagUrl}
          alt=""
          loading="lazy"
          sx={{
            width: size === 'md' ? 20 : 16,
            height: imgH,
            objectFit: 'cover',
            borderRadius: 0.25,
            boxShadow: '0 0 0 1px rgba(15,23,42,0.12)',
            flexShrink: 0,
          }}
        />
      ) : (
        <Box component="span" sx={{ fontSize: size === 'md' ? '1rem' : '0.9rem', lineHeight: 1 }}>
          {meta.flagEmoji}
        </Box>
      )}
      <Typography
        component="span"
        sx={{
          fontWeight: 600,
          fontSize: size === 'md' ? '0.85rem' : '0.75rem',
          color: brandColors.navy,
          whiteSpace: 'nowrap',
        }}
      >
        {meta.name}
      </Typography>
    </Box>
  );
};

/** Age-wise rankings with skill totals and city / zone / state / national filters. */
const ContestLeaderboardTabPanel: React.FC<ContestLeaderboardTabPanelProps> = ({
  sportName,
  ageCategory,
  mode = 'contest',
}) => {
  const isSelfEval = mode === 'self_eval';
  const apiPath = isSelfEval
    ? `/contest-catalog/${encodeURIComponent(sportName)}/self-eval-leaderboard`
    : `/contest-catalog/${encodeURIComponent(sportName)}/leaderboard`;
  const [level, setLevel] = useState<LeaderboardLevel>('national');
  const [city, setCity] = useState('');
  const [zone, setZone] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  const [skillId, setSkillId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [skills, setSkills] = useState<SkillOption[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [zones, setZones] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [countries, setCountries] = useState<CountryOption[]>(PLATFORM_COUNTRY_OPTIONS);
  const [view, setView] = useState<LeaderboardView>('standings');
  const [selectedPlayerKey, setSelectedPlayerKey] = useState<string | null>(null);

  const scope = skillId ? 'skill' : 'total';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const params: Record<string, string> = {
          ageCategory,
          scope,
          level,
          limit: '50',
        };
        if (skillId) params.skillId = skillId;
        if (level === 'city' && city) params.city = city;
        if (level === 'zone' && zone) params.zone = zone;
        if (level === 'state' && state) params.state = state;
        if (country) params.country = country;

        const res = await api.get(apiPath, { params });
        if (cancelled) return;
        const data = res.data?.data;
        setEntries(Array.isArray(data?.entries) ? data.entries : []);
        setSkills(Array.isArray(data?.filterOptions?.skills) ? data.filterOptions.skills : []);
        setCities(Array.isArray(data?.filterOptions?.cities) ? data.filterOptions.cities : []);
        setZones(Array.isArray(data?.filterOptions?.zones) ? data.filterOptions.zones : []);
        setStates(Array.isArray(data?.filterOptions?.states) ? data.filterOptions.states : []);
        setCountries(
          mergeCountryOptions(
            Array.isArray(data?.filterOptions?.countries) ? data.filterOptions.countries : [],
          ),
        );
      } catch (e: unknown) {
        if (cancelled) return;
        const msg =
          (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          (e as Error)?.message ||
          'Failed to load leaderboard';
        setError(msg);
        setEntries([]);
        setCountries(PLATFORM_COUNTRY_OPTIONS);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [apiPath, sportName, ageCategory, skillId, scope, level, city, zone, state, country]);

  useEffect(() => {
    setSelectedPlayerKey(null);
  }, [sportName, ageCategory, skillId, level, city, zone, state, country]);

  useEffect(() => {
    if (level === 'city' && !city && cities[0]) setCity(cities[0]);
    if (level === 'zone' && !zone && zones[0]) setZone(zones[0]);
    if (level === 'state' && !state && states[0]) setState(states[0]);
  }, [level, cities, zones, states, city, zone, state]);

  const needsGeoPick =
    (level === 'city' && !city) || (level === 'zone' && !zone) || (level === 'state' && !state);

  const titleSuffix = useMemo(() => {
    if (level === 'city' && city) return ` · ${city}`;
    if (level === 'zone' && zone) return ` · ${zone} Zone`;
    if (level === 'state' && state) return ` · ${state}`;
    if (country) {
      const meta = resolveCountryMeta(country);
      return meta ? ` · ${meta.flagEmoji} ${meta.name}` : ` · ${country}`;
    }
    return ' · National';
  }, [level, city, zone, state, country]);

  return (
    <ContestFrameShell
      tone="secondary"
      integratedTitle={`${sportName} ${isSelfEval ? 'Self-eval standings' : 'Leaderboard'} · ${ageCategory}${titleSuffix}`}
      titleAccent={brandColors.green}
      frameHeight={{ xs: 'auto', lg: 'calc(100dvh - 360px)' }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          p: { xs: 1.5, md: 2 },
          bgcolor: brandColors.ice,
          overflow: 'auto',
        }}
      >
        <Stack spacing={1.25}>
          {isSelfEval ? (
            <Alert severity="info" sx={{ borderRadius: 2, py: 0.75 }}>
              Community self-assessment standings for prep and comparison — separate from the official contest video leaderboard.
            </Alert>
          ) : null}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '0.78rem',
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              color: brandColors.slate,
              fontFamily: '"Sora", Arial, sans-serif',
            }}
          >
            Competition level
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={level}
            onChange={(_, next) => {
              if (next) setLevel(next);
            }}
            sx={{
              flexWrap: 'wrap',
              gap: 0.75,
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '8px !important',
                border: '1px solid #cbd5e1 !important',
                px: 1.5,
              },
              '& .Mui-selected': {
                bgcolor: `${brandColors.green} !important`,
                color: '#fff !important',
              },
            }}
          >
            {LEVELS.map((item) => (
              <ToggleButton key={item.id} value={item.id}>
                {item.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} useFlexGap flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Country</InputLabel>
              <Select
                label="Country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                renderValue={(selected) => {
                  if (!selected) return 'All countries';
                  const opt = countries.find((c) => c.name === selected);
                  return (
                    <CountryFlagLabel country={selected} countryCode={opt?.code} size="md" />
                  );
                }}
              >
                <MenuItem value="">
                  <em>All countries</em>
                </MenuItem>
                {countries.map((c) => (
                  <MenuItem key={c.name} value={c.name}>
                    <CountryFlagLabel country={c.name} countryCode={c.code} size="md" />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {level === 'city' && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>City</InputLabel>
                <Select label="City" value={city} onChange={(e) => setCity(e.target.value)}>
                  {cities.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {level === 'zone' && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Zone</InputLabel>
                <Select label="Zone" value={zone} onChange={(e) => setZone(e.target.value)}>
                  {zones.map((z) => (
                    <MenuItem key={z} value={z}>
                      {z}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {level === 'state' && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>State</InputLabel>
                <Select label="State" value={state} onChange={(e) => setState(e.target.value)}>
                  {states.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
              <InputLabel>Skill</InputLabel>
              <Select label="Skill" value={skillId} onChange={(e) => setSkillId(e.target.value)}>
                <MenuItem value="">
                  <em>All skills (total score)</em>
                </MenuItem>
                {skills.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        <Tabs
          value={view}
          onChange={(_, next: LeaderboardView) => setView(next)}
          sx={{
            minHeight: 36,
            borderBottom: '1px solid #e2e8f0',
            '& .MuiTab-root': {
              minHeight: 36,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.85rem',
              color: brandColors.slate,
            },
            '& .Mui-selected': { color: `${brandColors.green} !important` },
            '& .MuiTabs-indicator': { bgcolor: brandColors.green, height: 3 },
          }}
        >
          <Tab value="standings" label="Standings" />
          <Tab value="map" label="Global map" />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : needsGeoPick ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Select a {level} to view rankings.
          </Alert>
        ) : entries.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            No {isSelfEval ? 'self-evaluation standings' : 'approved scores'} for {ageCategory}
            {titleSuffix} yet.
            {isSelfEval
              ? ' Submit a self evaluation or seed community demo data from admin.'
              : ' Seed test data from admin or submit and approve entries.'}
          </Alert>
        ) : view === 'map' ? (
          <ContestLeaderboardMapPanel
            entries={entries}
            selectedPlayerKey={selectedPlayerKey}
            onSelectPlayer={setSelectedPlayerKey}
          />
        ) : (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
              bgcolor: brandColors.white,
            }}
          >
            <TableContainer sx={{ maxHeight: { xs: 420, lg: 'calc(100dvh - 500px)' } }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Player</TableCell>
                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Country</TableCell>
                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }} align="right">
                      {skillId ? 'Skill score' : 'Total score'}
                    </TableCell>
                    {!skillId && (
                      <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }} align="center">
                        Skills
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }} align="center">
                      Rating
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((row) => {
                    const playerKey = `${row.rank}-${row.playerName}-${String(row.city || '')
                      .trim()
                      .toLowerCase()}`;
                    const selected = selectedPlayerKey === playerKey;
                    return (
                      <TableRow
                        key={playerKey}
                        hover
                        selected={selected}
                        onClick={() => {
                          setSelectedPlayerKey(selected ? null : playerKey);
                          setView('map');
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                      <TableCell sx={{ fontWeight: 800, color: brandColors.green }}>{row.rank}</TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 700, color: brandColors.navy, fontSize: '0.9rem' }}>
                          {row.playerName}
                        </Typography>
                        {row.organizationName ? (
                          <Typography variant="caption" color="text.secondary">
                            {row.organizationName}
                          </Typography>
                        ) : null}
                        {skillId && row.skillTitle ? (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {row.skillTitle}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <CountryFlagLabel country={row.country} countryCode={row.countryCode} />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {row.city ? <Chip size="small" label={row.city} /> : null}
                          {row.zone ? <Chip size="small" variant="outlined" label={row.zone} /> : null}
                          {row.state ? (
                            <Chip size="small" variant="outlined" color="default" label={row.state} />
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 800, color: brandColors.navy }}>
                          {Number(row.totalScore).toFixed(row.totalScore % 1 ? 1 : 0)}
                        </Typography>
                        {!skillId && row.avgScore != null ? (
                          <Typography variant="caption" color="text.secondary">
                            avg {Number(row.avgScore).toFixed(1)}
                          </Typography>
                        ) : null}
                      </TableCell>
                      {!skillId && (
                        <TableCell align="center">
                          <Chip size="small" color="success" variant="outlined" label={row.skillsCounted} />
                        </TableCell>
                      )}
                      <TableCell align="center">
                        {row.avgRating != null ? Number(row.avgRating).toFixed(1) : '—'}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>
    </ContestFrameShell>
  );
};

export default ContestLeaderboardTabPanel;
